import React, { useState, ReactNode, ComponentType, SVGProps, useEffect, useRef, JSX } from 'react';
import { useSpring, animated, config as springConfig } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import * as S from './Home.styles';
import SimpleFooter from '../../shared/components/layout/SimpleFooter';

const diplomaIconSrc = require('../../assets/icons/diploma.svg');
const calendarIconSrc = require('../../assets/icons/calendar.svg');
const playIconSrc = require('../../assets/icons/play.svg');
const stackedCoinIconSrc = require('../../assets/icons/stacked-coin.svg');
const logoNoPaddingSrc = require('../../assets/logo-no-padding.png');


interface IconWrapperProps extends SVGProps<SVGSVGElement> {
  icon: ComponentType<SVGProps<SVGSVGElement>> | string | { default: ComponentType<SVGProps<SVGSVGElement>> | string };
  alt?: string;
}

const IconWrapper: React.FC<IconWrapperProps> = ({ icon: iconAsProp, alt, ...rest }) => {
  let iconToRender: any = iconAsProp;

  if (typeof iconToRender === 'object' && iconToRender !== null && iconToRender.default !== undefined) {
    iconToRender = iconToRender.default;
  }

  if (typeof iconToRender === 'function') {
    const Component = iconToRender as ComponentType<SVGProps<SVGSVGElement>>;
    return <Component {...rest} />;
  }

  if (typeof iconToRender === 'string') {
    const { className, style } = rest as any;
    return <img src={iconToRender} alt={alt || "icon"} className={className} style={style} />;
  }
  
  if (typeof iconToRender === 'object' && iconToRender !== null && typeof (iconToRender as any).$$typeof === 'symbol') {
     if ((iconToRender as any).$$typeof.toString() === 'Symbol(react.element)') {
        return iconToRender as React.ReactElement; 
     }
  }
  return null;
};

interface ChildrenProps {
  children: ReactNode;
}

const StepItem: React.FC<ChildrenProps> = ({ children }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const showAnimation = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px)' : 'translateY(50px)',
    config: springConfig.gentle,
  });
  const [hovered, setHovered] = useState(false);
  const hoverAnimation = useSpring({
    transform: hovered ? 'scale(1.05)' : 'scale(1)',
    config: springConfig.stiff,
  });

  return (
    <S.AnimatedStyledListItem
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{...showAnimation, ...hoverAnimation}}
    >
      {children}
    </S.AnimatedStyledListItem>
  );
};

const AnimatedElement: React.FC<{ as?: keyof JSX.IntrinsicElements | ComponentType<any>, children: ReactNode, style?: any, inViewConfig?: any, yOffset?: number } & any> = ({ as: Component = animated.div, children, style, inViewConfig = { triggerOnce: true, threshold: 0.1 }, yOffset = 30, ...props }) => {
    const [ref, inView] = useInView(inViewConfig);
    const animation = useSpring({
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0px)' : `translateY(${yOffset}px)`,
      config: springConfig.gentle,
      ...style
    });
  
    const AnimatedComp = Component as any; // Type assertion
    return (
      <AnimatedComp ref={ref} style={animation} {...props}>
        {children}
      </AnimatedComp>
    );
};


const Home: React.FC = () => {
  const navigate = useNavigate();
  const heroRef = useRef<HTMLElement>(null);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const titleSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: springConfig.gentle,
    delay: 200,
  });

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottomEdgeScrolledPast = heroRef.current.offsetTop + heroRef.current.offsetHeight;
        setIsHeaderHidden(window.scrollY > heroBottomEdgeScrolledPast);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const headerAnimation = useSpring({
    transform: isHeaderHidden ? 'translateY(-100%)' : 'translateY(0%)',
    config: springConfig.stiff,
  });
  
  return (
    <>
      <S.SiteHeader style={headerAnimation}>
        <S.ContainerHero>
          <S.LogoH1>
            <a href="/">
              <S.LogoImage src={logoNoPaddingSrc} alt="Illuminate Logo" />
            </a>
          </S.LogoH1>
          <S.MobileMenuToggle onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} $isOpen={isMobileMenuOpen}>
            <span></span>
          </S.MobileMenuToggle>
          <S.MainNav $isOpen={isMobileMenuOpen}>
            <a href="#whyJoin" onClick={() => setIsMobileMenuOpen(false)}>Why Join?</a>
            <a href="#howItWorks" onClick={() => setIsMobileMenuOpen(false)}>How It Works</a>
            <S.StyledButtonLink href="/Login/student" $outline onClick={() => setIsMobileMenuOpen(false)}>Student Login</S.StyledButtonLink>
            <S.StyledButtonLink href="/Login/tutor" $outline onClick={() => setIsMobileMenuOpen(false)}>Tutor Login</S.StyledButtonLink>
          </S.MainNav>
        </S.ContainerHero>
      </S.SiteHeader>

      <S.HeroSection id="hero" ref={heroRef}>
        <S.ContainerHero style={titleSpring}>
          <S.HeroTitle>Connect. Learn. Illuminate.</S.HeroTitle>
          <S.HeroSubtitle>
            Join the Illuminate team and become part of a community that
            strives to get better. Whether you're a student or a tutor,
            Illuminate is the place for you.
          </S.HeroSubtitle>
          {/**
           * 
          <AnimatedElement as={S.ExcelCTAButtons} yOffset={30} style={{marginTop: '2rem'}}>
            <S.StyledButtonLink as="button" onClick={() => navigate('/search', { state: { from: 'home' } })} $isExcelCTA>Search Classes</S.StyledButtonLink>
          </AnimatedElement>

           */}
        </S.ContainerHero>
      </S.HeroSection>

      <S.WhyJoinSection id="whyJoin">
        <S.ContainerHero>
            <AnimatedElement as={S.StrokedTextH2} yOffset={100} style={{delay: 300}}>Why Join?</AnimatedElement>
          <S.WhyJoinGrid>
            <S.WhyJoinColumn>
              <AnimatedElement as={S.WhyJoinHeading} yOffset={50} style={{delay:350}}>For Students</AnimatedElement>
              <S.WhyJoinList>
                <AnimatedElement as={S.WhyJoinItem} yOffset={50} style={{delay:500}}>
                  <IconWrapper icon={diplomaIconSrc} alt="Diploma Icon" />
                  <div>
                    Get help from fellow students who understand exactly
                    what you're doing in class.
                  </div>
                </AnimatedElement>
                <AnimatedElement as={S.WhyJoinItem} yOffset={50} style={{delay:550}}>
                  <IconWrapper icon={stackedCoinIconSrc} alt="Stacked Coin Icon" />
                  <div>
                    Request support when you need it—whether it's one topic
                    or ongoing help.
                  </div>
                </AnimatedElement>
              </S.WhyJoinList>
            </S.WhyJoinColumn>
            <S.WhyJoinColumn>
              <AnimatedElement as={S.WhyJoinHeading} yOffset={50} style={{delay:350}}>For Tutors</AnimatedElement>
              <S.WhyJoinList>
                <AnimatedElement as={S.WhyJoinItem} yOffset={50} style={{delay:500}}>
                  <IconWrapper icon={calendarIconSrc} alt="Calendar Icon" />
                  <div>
                    Earn money while making a difference—a flexible way to
                    turn your skills into income.
                  </div>
                </AnimatedElement>
                <AnimatedElement as={S.WhyJoinItem} yOffset={50} style={{delay:550}}>
                  <IconWrapper icon={playIconSrc} alt="Play Icon" />
                  <div>
                    Develop real skills in communication, teaching, and
                    time management.
                  </div>
                </AnimatedElement>
              </S.WhyJoinList>
            </S.WhyJoinColumn>
          </S.WhyJoinGrid>
        </S.ContainerHero>
      </S.WhyJoinSection>

      <S.HowItWorksSection id="howItWorks">
        <S.ContainerHero>
          <AnimatedElement as={S.HowItWorksTitle} yOffset={20}>How It Works</AnimatedElement>
          <S.HowItWorksGrid>
            <S.HowItWorksColumn>
              <AnimatedElement as={S.GenericAnimatedH4} yOffset={50}>For Students</AnimatedElement>
              <S.HowItWorksStepsList>
                <StepItem>
                  <strong>Sign up</strong> and create your student profile
                </StepItem>
                <StepItem>
                  <strong>Browse</strong> available classes and tutors
                </StepItem>
                <StepItem>
                  <strong>Book</strong> sessions that fit your schedule
                </StepItem>
                <StepItem>
                  <strong>Learn</strong> from experienced peer tutors
                </StepItem>
              </S.HowItWorksStepsList>
            </S.HowItWorksColumn>
            <S.HowItWorksColumn>
                <AnimatedElement as={S.GenericAnimatedH4} yOffset={50}>For Tutors</AnimatedElement>
              <S.HowItWorksStepsList>
                <StepItem>
                  <strong>Apply</strong> to become a verified tutor
                </StepItem>
                <StepItem>
                  <strong>Create</strong> classes in your areas of expertise
                </StepItem>
                <StepItem>
                  <strong>Set</strong> your availability and rates
                </StepItem>
                <StepItem>
                  <strong>Teach</strong> and earn while helping peers
                </StepItem>
              </S.HowItWorksStepsList>
            </S.HowItWorksColumn>
          </S.HowItWorksGrid>
        </S.ContainerHero>
      </S.HowItWorksSection>

      <S.ExcelCTASection id="excelCTA">
        <AnimatedElement as={S.ExcelCTAOverlay} yOffset={0} style={{opacity: 1}}/>
        <S.ExcelCTAContent>
          <AnimatedElement as={S.ExcelCTATitle} yOffset={30}>
            Get Ready to Excel<br />in Your Studies
          </AnimatedElement>
          <AnimatedElement as={S.ExcelCTAText} yOffset={30}>
            Join Illuminate and start your journey toward academic success. Our app will be released soon. Sign up to stay updated and be the first to access our tutoring platform.
          </AnimatedElement>
          <AnimatedElement as={S.ExcelCTAButtons} yOffset={30}>
            <S.StyledButtonLink href="/Login/student" target="_blank" rel="noopener noreferrer" $isExcelCTA>Students</S.StyledButtonLink>
            <S.StyledButtonLink href="/Login/tutor" target="_blank" rel="noopener noreferrer" $isExcelCTA>Tutors</S.StyledButtonLink>
          </AnimatedElement>
        </S.ExcelCTAContent>
      </S.ExcelCTASection>

      <SimpleFooter />
    </>
  );
};

export default Home;